import { useCallback, useEffect, useState } from 'react';
import { config } from '../../../../config';
import { ChatMessage } from '../../../../types/case-workspace';
import { SimulationChatMessage } from '../../../../types/ombudsman';

interface UseCaseWorkspaceChatParams {
  simulationId: string | null;
  streamingMessages: SimulationChatMessage[];
}

export function useCaseWorkspaceChat({
  simulationId,
  streamingMessages,
}: UseCaseWorkspaceChatParams) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const chatMessages: ChatMessage[] = streamingMessages.map((msg, index) => ({
      id: `stream-${index}`,
      role: 'assistant',
      content: msg.message,
      timestamp: new Date().toISOString(),
    }));
    setMessages(chatMessages);
  }, [streamingMessages]);

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || !simulationId) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    const assistantMessageId = (Date.now() + 1).toString();
    const token = localStorage.getItem('token');

    if (!token) {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: assistantMessageId,
        role: 'assistant' as const,
        content: 'Authentication error. Please log in again.',
        timestamp: new Date().toISOString(),
      }]);
      return;
    }

    try {
      const streamUrl = `${config.API_URL}/v1/ombudsman-decisions/simulations/${simulationId}/chat/stream?message=${encodeURIComponent(message)}`;
      const response = await fetch(streamUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok || !response.body) {
        throw new Error('Failed to connect to chat stream');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const rawEvent of events) {
          if (!rawEvent.trim()) continue;

          const lines = rawEvent.split('\n');
          let eventType: string | null = null;
          let dataPayload = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataPayload += line.slice(5).trim();
            }
          }

          if (!eventType || !dataPayload) continue;

          try {
            const data = JSON.parse(dataPayload);

            if (eventType === 'delta' && data.text) {
              setIsTyping(false);
              setMessages(prev => upsertAssistantMessage(prev, assistantMessageId, data.text));
            } else if (eventType === 'error') {
              setIsTyping(false);
              setMessages(prev => upsertAssistantMessage(
                prev,
                assistantMessageId,
                'Sorry, there was an error processing your question.',
                true
              ));
            }
          } catch (err) {
            console.warn('Failed to parse SSE data', err);
          }
        }
      }
    } catch (error) {
      console.error('Chat stream error:', error);
      setIsTyping(false);
      setMessages(prev => upsertAssistantMessage(
        prev,
        assistantMessageId,
        'Sorry, there was an error connecting to chat.',
        true
      ));
    }
  }, [simulationId]);

  return {
    messages,
    inputMessage,
    isTyping,
    setInputMessage,
    sendMessage,
  };
}

function upsertAssistantMessage(
  messages: ChatMessage[],
  assistantMessageId: string,
  content: string,
  replace = false
): ChatMessage[] {
  const assistantExists = messages.some(msg => msg.id === assistantMessageId);

  if (!assistantExists) {
    return [...messages, {
      id: assistantMessageId,
      role: 'assistant' as const,
      content,
      timestamp: new Date().toISOString(),
    }];
  }

  return messages.map(msg =>
    msg.id === assistantMessageId
      ? { ...msg, content: replace ? content : msg.content + content }
      : msg
  );
}
